import { type DateRange, getRange } from "@pentacode/core";
import type { DateString } from "@pentacode/openapi";
import { type TemplateResult, html } from "lit";
import { createRoute, lazyElement } from "../../../lib/tsr/router";
import { redirectTo } from "../../app-router-context";
import { employeeRoute } from "../../app-routes";
import type { EmployeeLayoutRoute } from "./employee.route";
import { employeeAdvancesQuery, employeeTimeQuery } from "./time.queries";

function validateRange(search: Record<string, unknown>): Partial<DateRange> {
    return { from: search.from as DateString | undefined, to: search.to as DateString | undefined };
}

export function createEmployeeTimeRoutes(parent: EmployeeLayoutRoute) {
    const time = createRoute({
        getParentRoute: () => parent,
        path: "time",
        staticData: {
            title: ({ match, context }) => `Arbeitszeiten: ${context.app.getEmployee(match.params.employeeId)?.name}`,
            menu: employeeRoute("time"),
        },
        validateSearch: (search: Record<string, unknown>): Partial<DateRange> & { timeEntry?: string } => ({
            ...validateRange(search),
            timeEntry: search.timeEntry as string | undefined,
        }),
        beforeLoad: ({ search, params }): void => {
            if (!search.from || !search.to) {
                redirectTo(`/employees/${params.employeeId}/time`, { ...search, ...getRange(new Date(), "month") });
            }
        },
        loaderDeps: ({ search }) => ({ from: search.from!, to: search.to! }),
        loader: ({ deps, params, context }) =>
            context.queryClient.fetchQuery({
                ...employeeTimeQuery(context.app, params.employeeId, deps),
                staleTime: 30_000,
            }),
        component: ({ match }): TemplateResult =>
            html`<ptc-employees-time-single
                class="fullbleed"
                .employeeId=${match.params.employeeId}
                .range=${{ from: match.search.from, to: match.search.to }}
                .timeEntry=${match.search.timeEntry}
                .data=${match.loaderData}
            ></ptc-employees-time-single>`,
    }).lazy(lazyElement(() => import("./employees-time-single")));

    const advances = createRoute({
        getParentRoute: () => parent,
        path: "advances",
        staticData: {
            title: ({ match, context }) => `Vorschüsse: ${context.app.getEmployee(match.params.employeeId)?.name}`,
            menu: employeeRoute("advances"),
        },
        validateSearch: validateRange,
        beforeLoad: ({ search, params }): void => {
            if (!search.from || !search.to) {
                redirectTo(`/employees/${params.employeeId}/advances`, {
                    ...search,
                    ...getRange(new Date(), "month"),
                });
            }
        },
        loaderDeps: ({ search }) => ({ from: search.from!, to: search.to! }),
        loader: ({ deps, params, context }) =>
            context.queryClient.fetchQuery({
                ...employeeAdvancesQuery(context.app, params.employeeId, deps),
                staleTime: 30_000,
            }),
        component: ({ match }): TemplateResult =>
            html`<ptc-employees-advances-single
                class="fullbleed"
                .employeeId=${match.params.employeeId}
                .range=${{ from: match.search.from, to: match.search.to }}
                .entries=${match.loaderData ?? []}
            ></ptc-employees-advances-single>`,
    }).lazy(lazyElement(() => import("./employees-advances-single")));

    return [time, advances] as const;
}
